import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_ReferenceColors() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🎨 CSS: Colors</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS supports multiple ways to define colors, including names, HEX, RGB,
        HSL, and modern functions like <code>rgba()</code> and{" "}
        <code>hsla()</code>. Colors play a key role in design, accessibility,
        and branding.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#166534" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            CSS has <b>140+ named colors</b> (like red, blue, lime).
          </li>
          <li>
            HEX format: <code>#RRGGBB</code> (e.g. <code>#ff0000</code>).
          </li>
          <li>
            RGB format: <code>rgb(255, 0, 0)</code>.
          </li>
          <li>
            RGBA adds <b>transparency</b>: <code>rgba(255, 0, 0, 0.5)</code>.
          </li>
          <li>
            HSL format: <code>hsl(0, 100%, 50%)</code>.
          </li>
          <li>HSLA adds alpha (opacity).</li>
        </ul>
      </div>

      {/* Example 1 */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Named Colors</h2>
        <p>
          CSS comes with built-in named colors like <code>red</code>,{" "}
          <code>blue</code>, <code>green</code>.
        </p>
        <CodeCopyBlock code={`p { color: red; }`} />
        <p style={{ fontSize: "28px", color: "red" }}>This is Red</p>
        <p style={{ fontSize: "28px", color: "blue" }}>This is Blue</p>
        <p style={{ fontSize: "28px", color: "green" }}>This is Green</p>
      </div>

      {/* Example 2 */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ HEX Colors</h2>
        <p>
          HEX uses <code>#RRGGBB</code>. Shorthand <code>#RGB</code> is also
          valid.
        </p>
        <CodeCopyBlock
          code={`h1 { color: #ff0000; }  /* Red */
p { color: #00ff00; }  /* Green */
span { color: #0000ff; }  /* Blue */`}
        />
        <p style={{ fontSize: "28px", color: "#ff0000" }}>#ff0000 (Red)</p>
        <p style={{ fontSize: "28px", color: "#00ff00" }}>#00ff00 (Green)</p>
        <p style={{ fontSize: "28px", color: "#0000ff" }}>#0000ff (Blue)</p>
      </div>

      {/* Example 3 */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ RGB & RGBA</h2>
        <p>
          RGB defines colors using red, green, blue values (0–255). RGBA adds
          alpha transparency (0–1).
        </p>
        <CodeCopyBlock
          code={`div { background-color: rgb(255, 99, 71); }  /* Tomato */
div { background-color: rgba(255, 99, 71, 0.5); }  /* 50% transparent */`}
        />
        <div
          style={{
            width: "120px",
            height: "60px",
            backgroundColor: "rgb(255, 99, 71)",
            margin: "10px",
            borderRadius: "6px",
          }}
        />
        <div
          style={{
            width: "120px",
            height: "60px",
            backgroundColor: "rgba(255, 99, 71, 0.5)",
            margin: "10px",
            borderRadius: "6px",
          }}
        />
      </div>

      {/* Example 4 */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ HSL & HSLA</h2>
        <p>
          HSL stands for Hue (0–360), Saturation (%), and Lightness (%). HSLA
          includes Alpha for transparency.
        </p>
        <CodeCopyBlock
          code={`div { background-color: hsl(200, 100%, 50%); }  /* Bright Blue */
div { background-color: hsla(200, 100%, 50%, 0.5); }  /* Transparent Blue */`}
        />
        <div
          style={{
            width: "120px",
            height: "60px",
            backgroundColor: "hsl(200, 100%, 50%)",
            margin: "10px",
            borderRadius: "6px",
          }}
        />
        <div
          style={{
            width: "120px",
            height: "60px",
            backgroundColor: "hsla(200, 100%, 50%, 0.5)",
            margin: "10px",
            borderRadius: "6px",
          }}
        />
      </div>

      {/* Example 5 */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ Transparency with Opacity</h2>
        <p>
          You can also make elements transparent using the <code>opacity</code>{" "}
          property.
        </p>
        <CodeCopyBlock
          code={`div {
  background-color: orange;
  opacity: 0.5; /* 50% transparent */
}`}
        />
        <div
          style={{
            width: "120px",
            height: "60px",
            backgroundColor: "orange",
            opacity: "0.5",
            margin: "10px",
            borderRadius: "6px",
          }}
        />
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde047)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#854d0e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about CSS Color Module Level 4 (<code>color()</code>{" "}
            function).
          </li>
          <li>
            Use <code>currentColor</code> to reuse the element’s color
            dynamically.
          </li>
          <li>Test contrast for accessibility (WCAG guidelines).</li>
          <li>
            Explore gradients (<code>linear-gradient</code>,{" "}
            <code>radial-gradient</code>).
          </li>
        </ul>
      </div>
    </div>
  );
}
